import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { updateUser } from "../../RTK/users/updateUser ";
import { toast } from "react-toastify";
import ReactSwitch from "react-switch";
import {
  History,
  ArrowUpRight,
  ArrowDownLeft,
  ArrowRight,
  ArrowLeft,
  User,
  MapPin,
  Shield,
  Wallet,
  ShoppingCart,
  Calendar,
  Phone,
  Mail,
  Key,
  CreditCard,
  AlertCircle,
  CheckCircle,
  Clock,
  Star,
  UserPlus,
  Activity,
  Check,
  ChevronDown,
  Loader2,
  Bell,
  DollarSign,
  TrendingUp,
  TrendingDown,
  ChevronLeft,
  Edit,
  Filter,
  XCircle,
  CheckCircle2,
  Package,
} from "lucide-react";

import { fetchuserLevels } from "../../RTK/userlevels/GetAll";
import ConfirmDelete from "./popups/ConfirmDelete";
import { deletuser } from "../../RTK/users/DeleteUser";
import { fetchUsers, fetchUserDetails } from "../../RTK/users/GetAllUsers";
import NotificationPopup from "./popups/NotificationPopup ";
import { sendNotification } from "../../RTK/users/sendNotification ";

// استيراد الـ API والمكونات الجديدة
import {
  fetchFinancialDetails,
  fetchTransactionHistory,
  fetchWalletLogs,
  resetTransactionHistory,
  resetWalletLogs,
  resetFinancialDetails,
} from "../../RTK/users/userSlice";
import TransactionCard from "./components/TransactionCard";
import WalletLogCard from "./components/WalletLogCard";
import LoadingSpinner from "./components/LoadingSpinner";

import { usePermissions } from "../../contexts/PermissionsContext";
import { Lock } from "lucide-react";

const UserDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
const { hasPermission } = usePermissions(); // ✅ إضافة

  // ✅ فحص الصلاحيات
  const canUpdateUser = hasPermission("update_user");
  const canDeleteUser = hasPermission("delete_user");
  const canSendNotifications = hasPermission("send_notifications");

  const [activeSection, setActiveSection] = useState("المعلومات الأساسية");
  const [isEditingWallet, setIsEditingWallet] = useState(false);
  const [isEditingMaxDebt, setIsEditingMaxDebt] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditingDebt, setIsEditingDebt] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [deleteUserId, setDeleteUserId] = useState(null);
  const [isNotificationPopupVisible, setIsNotificationPopupVisible] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // User data state
  const [walletBalance, setWalletBalance] = useState(0);
  const [maxDebtLimit, setMaxDebtLimit] = useState(0);
  const [currentDebt, setCurrentDebt] = useState("0");
  const [isManualLevel, setIsManualLevel] = useState(false);

  // ✅ Filters state
  const [transactionFilters, setTransactionFilters] = useState({
    type: "",
    status: "",
    dateFrom: "",
    dateTo: "",
  });

  // ✅ Refs for Infinite Scroll
  const transactionsLoadMoreRef = useRef(null);
  const walletLogsLoadMoreRef = useRef(null);

  // Redux selectors
  const { userLevels, loading } = useSelector((state) => state.userLevels);
  const { isLoading: isNotificationLoading } = useSelector((state) => state.notifications);
  const { selectedUser, isFetchingDetails } = useSelector((state) => state.users);

  // ✅ Selectors للبيانات المالية
  const {
    financialDetails,
    transactionHistory,
    walletLogs,
    loading: financialLoading,
    error: financialError,
  } = useSelector((state) => state.userFinancial);

  // ✅ استخراج البيانات من financialDetails
  const financialSummary = financialDetails?.financial_summary;
  const productPerformance = financialDetails?.product_performance || [];
  const accountStatus = financialDetails?.account_status;
  const recentActivity = financialDetails?.recent_activity;

  // Fetch ONLY basic user details on mount
  useEffect(() => {
    if (id) {
      dispatch(fetchUserDetails(id))
        .unwrap()
        .then((userData) => {
          const financialStats = userData?.data?.financial_stats;
          setWalletBalance(financialStats?.wallet_balance || 0);
          setMaxDebtLimit(financialStats?.debt?.max_debt_limit || 0);
          setCurrentDebt(financialStats?.debt?.current_debt || "0");
          setIsManualLevel(userData?.data?.membership_info?.user_level?.is_user_level_manual || false);
        })
        .catch((error) => {
          console.error("Error fetching user:", error);
          toast.error("فشل في تحميل بيانات المستخدم");
          navigate("/user");
        });
    }

    // ✅ Cleanup on unmount OR when id changes
    return () => {
      dispatch(resetTransactionHistory());
      dispatch(resetWalletLogs());
      dispatch(resetFinancialDetails()); 
    };
  }, [id, dispatch, navigate]);

  useEffect(() => {
    dispatch(fetchuserLevels())
      .unwrap()
      .catch(() => {
        toast.error("فشل في تحميل المستويات");
      });
  }, [dispatch]);

  // Fetch financial details ONLY when clicking on financial sections
  useEffect(() => {
    const financialSections = [
      "الملخص المالي الشامل",
      "أداء المنتجات",
      "حالة الحساب المتقدمة",
      "النشاط الأخير"
    ];

    if (financialSections.includes(activeSection) && id) {
      console.log("🔄 Loading financial details for section:", activeSection);
      dispatch(fetchFinancialDetails(id));
    }
  }, [activeSection, id, dispatch]);

  //Fetch transactions ONLY when clicking on transaction section OR filters change
  useEffect(() => {
    if (activeSection === "سجل المعاملات المالية" && id) {
      console.log("🔄 Loading transaction history...");
      dispatch(resetTransactionHistory());
      dispatch(
        fetchTransactionHistory({
          profileId: id,
          page: 1,
          pageSize: 20,
          ...transactionFilters,
        })
      );
    }
  }, [activeSection, id, dispatch, transactionFilters]);


  // Fetch wallet logs ONLY when clicking on wallet logs section
  useEffect(() => {
    if (activeSection === "سجل المحفظة التفصيلي" && id) {
      console.log("🔄 Loading wallet logs...");
      dispatch(resetWalletLogs());
      dispatch(
        fetchWalletLogs({
          profileId: id,
          page: 1,
          pageSize: 20,
        })
      );
    }
  }, [activeSection, id, dispatch]);

  // ✅ Infinite Scroll for Transactions
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          transactionHistory.hasMore &&
          !financialLoading.transactions &&
          activeSection === "سجل المعاملات المالية"
        ) {
          console.log("🔄 Loading more transactions...", {
            currentPage: transactionHistory.currentPage,
            totalPages: transactionHistory.totalPages,
            hasMore: transactionHistory.hasMore,
          });
          
          // ✅ إضافة dispatch هنا:
          dispatch(
            fetchTransactionHistory({
              profileId: id,
              page: transactionHistory.currentPage + 1,
              pageSize: 20,
              ...transactionFilters,
            })
          );
        }
      },
      { 
        threshold: 0.1,
        rootMargin: "100px"
      }
    );

    const currentRef = transactionsLoadMoreRef.current;
    if (currentRef && activeSection === "سجل المعاملات المالية") {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [
    dispatch,
    id,
    transactionHistory.hasMore,
    transactionHistory.currentPage,
    transactionFilters,
    financialLoading.transactions,
    activeSection,
  ]);

  

  // ✅ Infinite Scroll for Wallet Logs
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          walletLogs.hasMore &&
          !financialLoading.walletLogs &&
          activeSection === "سجل المحفظة التفصيلي"
        ) {
          console.log("🔄 Loading more wallet logs...", {
            currentPage: walletLogs.currentPage,
            totalPages: walletLogs.totalPages,
            hasMore: walletLogs.hasMore,
          });
          
          dispatch(
            fetchWalletLogs({
              profileId: id,
              page: walletLogs.currentPage + 1,
              pageSize: 20,
            })
          );
        }
      },
      { 
        threshold: 0.1,
        rootMargin: "100px"
      }
    );

    const currentRef = walletLogsLoadMoreRef.current;
    if (currentRef && activeSection === "سجل المحفظة التفصيلي") {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [
    dispatch,
    id,
    walletLogs.hasMore,
    walletLogs.currentPage,
    financialLoading.walletLogs,
    activeSection,
  ]);

  // ✅ Filter handlers
  const handleFilterChange = (filterName, value) => {
    setTransactionFilters((prev) => ({
      ...prev,
      [filterName]: value,
    }));
  };

  const clearFilters = () => {
    setTransactionFilters({
      type: "",
      status: "",
      dateFrom: "",
      dateTo: "",
    });
  };

  useEffect(() => {
    if (selectedUser?.data?.membership_info?.user_level?.is_user_level_manual !== undefined) {
      setIsManualLevel(selectedUser.data.membership_info.user_level.is_user_level_manual);
    }
  }, [selectedUser?.data?.membership_info?.user_level?.is_user_level_manual]);

  // ✅ Loading State
  if (isFetchingDetails) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 text-yellow-500 animate-spin" />
          <span className="text-gray-400">جاري تحميل بيانات المستخدم...</span>
        </div>
      </div>
    );
  }

  // ✅ Error State
  if (!selectedUser) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center p-4">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 md:w-16 md:h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl md:text-2xl font-bold text-white mb-2">المستخدم غير موجود</h2>
          <p className="text-sm md:text-base text-gray-400 mb-6">لم يتم العثور على المستخدم المطلوب</p>
          <button
            onClick={() => navigate("/user")}
            className="px-6 py-3 bg-yellow-500 text-black rounded-lg hover:bg-yellow-600 transition-colors"
          >
            العودة إلى قائمة المستخدمين
          </button>
        </div>
      </div>
    );
  }

  const {
    basic_info,
    location_info,
    dates,
    financial_stats,
    account_stats,
    security_info,
    membership_info,
    recent_transactions,
  } = selectedUser?.data || {};

  const profile_id = basic_info?.id;

  // ✅ Handlers
  const handleClossee = () => {
    setShowConfirm(false);
  };

  const handleDelete = async () => {
    try {
      await dispatch(deletuser(deleteUserId)).unwrap();
      await dispatch(fetchUsers()).unwrap();
      toast.success("تم حذف المستخدم بنجاح!");
      navigate("/user");
    } catch (error) {
      toast.error(error || "فشل حذف المستخدم");
    } finally {
      handleClossee();
      setDeleteUserId(null);
    }
  };

  const handleDeleteClick = (id) => {
    if (!canDeleteUser) {
      toast.error("ليس لديك صلاحية لحذف المستخدمين 🔒");
      return;
    }
    setDeleteUserId(id);
    setShowConfirm(true);
  };

  const handleNotificationSubmit = async (formData) => {
    if (!basic_info?.id) {
      toast.error("معرف المستخدم غير متوفر");
      return;
    }

    try {
      await dispatch(
        sendNotification({
          profile_id: basic_info.id,
          notificationData: formData,
        })
      ).unwrap();

      toast.success("تم إرسال الإشعار بنجاح");
      setIsNotificationPopupVisible(false);
    } catch (error) {
      toast.error(error || "فشل في إرسال الإشعار");
      console.error("Notification Error:", error);
    }
  };

  const handleSwitchToggle = (field, value) => {
    if (!canUpdateUser) {
      toast.error("ليس لديك صلاحية لتحديث بيانات المستخدمين 🔒");
      return;
    }

    setIsLoading(true);
    dispatch(updateUser({ userData: { [field]: value }, profile_id }))
      .unwrap()
      .then(() => {
        toast.success(`تم تحديث ${field} بنجاح`);
        dispatch(fetchUserDetails(id));
      })
      .catch(() => toast.error(`فشل في تحديث ${field}`))
      .finally(() => setIsLoading(false));
  };

  const handleSaveWallet = () => {

    if (!canUpdateUser) {
      toast.error("ليس لديك صلاحية لتحديث رصيد المحفظة 🔒");
      return;
    } 

    const numericBalance = parseFloat(walletBalance);
    if (isNaN(numericBalance)) {
      toast.error("الرجاء إدخال رقم صحيح");
      return;
    }

    setIsLoading(true);
    dispatch(
      updateUser({
        userData: { wallet_balance: numericBalance },
        profile_id,
      })
    )
      .unwrap()
      .then(() => {
        toast.success("تم تحديث رصيد المحفظة بنجاح");
        setIsEditingWallet(false);
        dispatch(fetchUserDetails(id));
      })
      .catch(() => toast.error("فشل في تحديث رصيد المحفظة"))
      .finally(() => setIsLoading(false));
  };

  const handleSaveDebt = async () => {
    if (!canUpdateUser) {
      toast.error("ليس لديك صلاحية لتحديث الدين 🔒");
      return;
    }

    const numericDebt = parseFloat(currentDebt);
    if (isNaN(numericDebt)) {
      toast.error("الرجاء إدخال رقم صحيح");
      return;
    }

    setIsLoading(true);
    try {
      await dispatch(
        updateUser({
          userData: { debt: numericDebt },
          profile_id,
        })
      ).unwrap();
      toast.success("تم تحديث الدين الحالي بنجاح");
      setIsEditingDebt(false);
      dispatch(fetchUserDetails(id));
    } catch (error) {
      toast.error("فشل تحديث الدين الحالي");
    } finally {
      setIsLoading(false);
    }
  };

  const handleLevelChange = async (levelId) => {
    if (!canUpdateUser) {
      toast.error("ليس لديك صلاحية لتحديث مستوى المستخدم 🔒");
      return;
    }

    if (!basic_info?.id) {
      toast.error("معرف المستخدم غير متوفر");
      return;
    }

    setIsLoading(true);
    try {
      await dispatch(
        updateUser({
          userData: {
            user_level: levelId,
            is_user_level_manual: true,
          },
          profile_id: basic_info.id,
        })
      ).unwrap();

      toast.success("تم تحديث مستوى المستخدم بنجاح");
      setIsDropdownOpen(false);
      dispatch(fetchuserLevels());
      dispatch(fetchUserDetails(id));
    } catch (error) {
      console.error("Level Change Error:", error);
      toast.error("فشل في تحديث المستوى");
    } finally {
      setIsLoading(false);
    }
  };

  const handleManualLevelToggle = async (checked) => {
    if (!canUpdateUser) {
      toast.error("ليس لديك صلاحية لتحديث إعدادات المستوى 🔒");
      return;
    }
    
    if (!basic_info?.id) {
      toast.error("معرف المستخدم غير متوفر");
      return;
    }

    setIsLoading(true);
    try {
      await dispatch(
        updateUser({
          userData: {
            is_user_level_manual: checked,
            user_level: membership_info?.user_level?.id,
          },
          profile_id: basic_info.id,
        })
      ).unwrap();

      setIsManualLevel(checked);
      toast.success("تم تحديث إعدادات المستوى بنجاح");
      dispatch(fetchUserDetails(id));
    } catch (error) {
      console.error("Manual Toggle Error:", error);
      toast.error("فشل في تحديث إعدادات المستوى");
      setIsManualLevel(!checked);
    } finally {
      setIsLoading(false);
    }
  };

  // ✅ Navigation buttons
  const navigationButtons = [
    { id: "المعلومات الأساسية", icon: <User size={18} /> },
    { id: "الملخص المالي الشامل", icon: <TrendingUp size={18} /> },
    { id: "أداء المنتجات", icon: <Package size={18} /> },
    { id: "إحصائيات الحساب", icon: <Activity size={18} /> },
    { id: "إعدادات الأمان", icon: <Shield size={18} /> },
    { id: "المعلومات المالية", icon: <Wallet size={18} /> },
    { id: "تفاصيل العضوية", icon: <UserPlus size={18} /> },
    { id: "إيداعات المستخدم", icon: <DollarSign size={18} /> },
    { id: "سجل المعاملات المالية", icon: <History size={18} /> },
    { id: "سجل المحفظة التفصيلي", icon: <Wallet size={18} /> },
    { id: "حالة الحساب المتقدمة", icon: <CheckCircle2 size={18} /> },
    { id: "النشاط الأخير", icon: <Calendar size={18} /> },
  ];

  const InfoItem = ({ label, value, icon }) => (
    <div className="bg-gray-900/50 backdrop-blur-sm p-4 rounded-xl border border-gray-800/50 hover:border-yellow-500/30 transition-all duration-300">
      <div className="flex items-center gap-2 text-gray-400 mb-2">
        {icon}
        <span className="text-xs md:text-sm">{label}</span>
      </div>
      <div className="text-white font-medium text-sm md:text-base">{value || "غير متوفر"}</div>
    </div>
  );

  const SwitchItem = ({ label, checked, onChange, icon, description }) => (
    <div className="bg-gray-900/50 backdrop-blur-sm p-4 rounded-xl border border-gray-800/50 hover:border-yellow-500/30 transition-all duration-300">
      <div className="flex items-center justify-between gap-4">
        <div className="space-y-1 flex-1 min-w-0">
          <div className="flex items-center gap-2">
            {icon}
            <span className="text-gray-300 text-sm md:text-base truncate">{label}</span>
            {!canUpdateUser && (
              <Lock className="w-3 h-3 text-slate-500 flex-shrink-0" />
            )}
          </div>
          {description && (
            <p className="text-xs text-gray-500 line-clamp-2">{description}</p>
          )}
        </div>
        <ReactSwitch
          checked={checked || false}
          onChange={onChange}
          onColor="#FCD34D"
          offColor="#374151"
          checkedIcon={false}
          uncheckedIcon={false}
          height={20}
          width={40}
          handleDiameter={16}
          disabled={isLoading || !canUpdateUser} // ✅ تعطيل لو مافيش صلاحية
        />
      </div>
    </div>
  );

  // الأقسام القديمة (كما هي)
  const renderBasicInfo = () => (
    <div className="space-y-6" dir="rtl">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 bg-yellow-500/10 rounded-lg">
          <User className="text-yellow-500 w-5 h-5" />
        </div>
        <h3 className="text-lg md:text-xl font-bold text-yellow-500">
          المعلومات الأساسية
        </h3>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <InfoItem
          label="اسم المستخدم"
          value={basic_info?.username}
          icon={<User size={16} />}
        />
        <InfoItem
          label="الاسم الكامل"
          value={basic_info?.full_name}
          icon={<User size={16} />}
        />
        <InfoItem
          label="رقم الهاتف"
          value={basic_info?.phone_number}
          icon={<Phone size={16} />}
        />
        <InfoItem
          label="البريد الإلكتروني"
          value={basic_info?.email}
          icon={<Mail size={16} />}
        />
      </div>

      <div className="mt-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-yellow-500/10 rounded-lg">
            <MapPin className="text-yellow-500 w-5 h-5" />
          </div>
          <h3 className="text-lg md:text-xl font-bold text-yellow-500">الموقع</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <InfoItem
            label="الدولة"
            value={location_info?.country}
            icon={<MapPin size={16} />}
          />
          <InfoItem
            label="رمز الاتصال"
            value={location_info?.calling_code}
            icon={<Phone size={16} />}
          />
        </div>
      </div>

      <div className="mt-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-yellow-500/10 rounded-lg">
            <Calendar className="text-yellow-500 w-5 h-5" />
          </div>
          <h3 className="text-lg md:text-xl font-bold text-yellow-500">التواريخ</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-gray-900/50 backdrop-blur-sm p-4 rounded-xl border border-gray-800/50 hover:border-yellow-500/30 transition-all duration-300">
            <div className="flex items-center gap-2 text-gray-400 mb-2">
              <Calendar size={16} />
              <span className="text-xs md:text-sm">تاريخ الانضمام</span>
            </div>
            <div className="text-white font-medium text-sm md:text-base flex items-center justify-between">
              <span>{dates?.registration_date ? dayjs(dates.registration_date).format("DD/MM/YYYY") : "غير متوفر"}</span>
              <span className="text-gray-400">{dates?.registration_date ? dayjs(dates.registration_date).format("h:mm A") : ""}</span>
            </div>
          </div>
          <div className="bg-gray-900/50 backdrop-blur-sm p-4 rounded-xl border border-gray-800/50 hover:border-yellow-500/30 transition-all duration-300">
            <div className="flex items-center gap-2 text-gray-400 mb-2">
              <Clock size={16} />
              <span className="text-xs md:text-sm">آخر دخول</span>
            </div>
            <div className="text-white font-medium text-sm md:text-base flex items-center justify-between">
              <span>{dates?.last_login ? dayjs(dates.last_login).format("DD/MM/YYYY") : "غير متوفر"}</span>
              <span className="text-gray-400">{dates?.last_login ? dayjs(dates.last_login).format("h:mm A") : ""}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderSecurityInfo = () => (
    <div className="space-y-6" dir="rtl">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-yellow-500/10 rounded-lg">
            <Shield className="text-yellow-500 w-5 h-5" />
          </div>
          <h3 className="text-lg md:text-xl font-bold text-yellow-500">
            إعدادات الأمان
          </h3>
        </div>
        
        {canDeleteUser ? (
          <button
            className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-red-500 rounded-lg shadow hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-400 transition-colors"
            onClick={() => handleDeleteClick(basic_info?.id)}
          >
            حذف المستخدم
          </button>
        ) : (
          <div className="flex items-center gap-2 px-4 py-2 bg-slate-800/50 rounded-lg border border-slate-700">
            <Lock className="w-4 h-4 text-slate-500" />
            <span className="text-slate-400 text-sm">حذف المستخدم</span>
          </div>
        )}
      </div>
      {showConfirm && canDeleteUser && (
        <ConfirmDelete onCancel={handleClossee} onConfirm={handleDelete} />
      )}
      {/* ✅ رسالة لو مافيش صلاحية تحديث */}
      {!canUpdateUser && (
        <div className="flex items-center gap-2 p-3 bg-orange-900/20 border border-orange-500/30 rounded-lg">
          <AlertCircle className="w-5 h-5 text-orange-400" />
          <span className="text-orange-300 text-sm">
            ليس لديك صلاحية لتعديل إعدادات الأمان
          </span>
        </div>
      )}

      <div className="space-y-3">
        <SwitchItem
          label="الحساب موثق"
          checked={security_info?.verification_status?.is_verified}
          onChange={(checked) => handleSwitchToggle("is_verified", checked)}
          icon={<AlertCircle size={16} />}
        />
        <SwitchItem
          label="البريد الإلكتروني موثق"
          checked={security_info?.verification_status?.is_email_verified}
          onChange={(checked) =>
            handleSwitchToggle("is_email_verified", checked)
          }
          icon={<Mail size={16} />}
        />
        <SwitchItem
          label="التحقق بخطوتين مفعل"
          checked={security_info?.two_factor_auth?.is_2fa_enabled}
          onChange={(checked) => handleSwitchToggle("is_2fa_enabled", checked)}
          icon={<Key size={16} />}
        />
        <SwitchItem
          label="الوصول إلى API"
          checked={security_info?.api_access?.can_use_api_key}
          onChange={(checked) => handleSwitchToggle("can_use_api_key", checked)}
          icon={<Key size={16} />}
        />
      </div>
    </div>
  );

  const renderFinancialStats = () => (
  <div className="space-y-6" dir="rtl">
    <div className="flex items-center gap-3 mb-4">
      <div className="p-2 bg-yellow-500/10 rounded-lg">
        <Wallet className="text-yellow-500 w-5 h-5" />
      </div>
      <h3 className="text-lg md:text-xl font-bold text-yellow-500">
        المعلومات المالية
      </h3>
    </div>

    {/* ✅ رسالة لو مافيش صلاحية */}
    {!canUpdateUser && (
      <div className="flex items-center gap-2 p-3 bg-orange-900/20 border border-orange-500/30 rounded-lg">
        <AlertCircle className="w-5 h-5 text-orange-400" />
        <span className="text-orange-300 text-sm">
          ليس لديك صلاحية لتعديل المعلومات المالية
        </span>
      </div>
    )}

    <div className="space-y-4">
      {/* رصيد المحفظة */}
      <div className="bg-gray-900/50 backdrop-blur-sm p-4 md:p-6 rounded-xl border border-gray-800/50 hover:border-yellow-500/30 transition-all duration-300">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex items-center gap-2">
            <CreditCard size={16} className="text-gray-400 flex-shrink-0" />
            <span className="text-gray-300 text-sm md:text-base">رصيد المحفظة</span>
          </div>
          {isEditingWallet ? (
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <input
                type="text"
                value={walletBalance}
                onChange={(e) => setWalletBalance(e.target.value)}
                disabled={!canUpdateUser}
                className="bg-gray-800 text-white px-3 py-2 rounded-lg w-24 text-left border border-gray-700 focus:border-yellow-500 text-sm disabled:opacity-50"
                placeholder="0.00"
              />
              <button
                onClick={handleSaveWallet}
                disabled={isLoading || !canUpdateUser}
                className="bg-yellow-500 text-black px-3 py-2 rounded-lg hover:bg-yellow-600 transition-colors disabled:opacity-50 text-sm"
              >
                {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "حفظ"}
              </button>
              <button
                onClick={() => setIsEditingWallet(false)}
                className="bg-gray-700 text-white px-3 py-2 rounded-lg hover:bg-gray-600 transition-colors text-sm"
              >
                إلغاء
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2 w-full sm:w-auto justify-between sm:justify-end">
              <span className="text-white font-bold text-base md:text-lg">
                {financial_stats?.wallet_balance || 0}$
              </span>
              {canUpdateUser ? (
                <button
                  onClick={() => setIsEditingWallet(true)}
                  className="bg-yellow-500 text-black px-3 py-2 rounded-lg hover:bg-yellow-600 transition-colors flex items-center gap-1 text-sm"
                >
                  <Edit size={14} />
                  <span className="hidden sm:inline">تعديل</span>
                </button>
              ) : (
                <div className="p-2 bg-slate-700/50 rounded-lg">
                  <Lock className="w-4 h-4 text-slate-500" />
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <InfoItem
        label="إجمالي المدفوعات"
        value={`${financial_stats?.total_payments || "0"}$`}
        icon={<CreditCard size={16} />}
      />
      
      {/* ✅ الدين الحالي - محمي بالصلاحية */}
      <div className="bg-gray-900/50 backdrop-blur-sm p-4 md:p-6 rounded-xl border border-gray-800/50 hover:border-yellow-500/30 transition-all duration-300">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex items-center gap-2">
            <AlertCircle size={16} className="text-gray-400 flex-shrink-0" />
            <span className="text-gray-300 text-sm md:text-base">الدين الحالي</span>
          </div>
          {isEditingDebt ? (
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <input
                type="text"
                value={currentDebt}
                onChange={(e) => setCurrentDebt(e.target.value)}
                disabled={!canUpdateUser}
                className="bg-gray-800 text-white px-3 py-2 rounded-lg w-24 text-left border border-gray-700 focus:border-yellow-500 text-sm disabled:opacity-50"
                placeholder="0.00"
              />
              <button
                onClick={handleSaveDebt}
                disabled={isLoading || !canUpdateUser}
                className="bg-yellow-500 text-black px-3 py-2 rounded-lg hover:bg-yellow-600 transition-colors disabled:opacity-50 text-sm"
              >
                {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "حفظ"}
              </button>
              <button
                onClick={() => setIsEditingDebt(false)}
                className="bg-gray-700 text-white px-3 py-2 rounded-lg hover:bg-gray-600 transition-colors text-sm"
              >
                إلغاء
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2 w-full sm:w-auto justify-between sm:justify-end">
              <span className="text-white font-bold text-base md:text-lg">
                {financial_stats?.debt?.current_debt || 0}$
              </span>
              {canUpdateUser ? (
                <button
                  onClick={() => setIsEditingDebt(true)}
                  className="bg-yellow-500 text-black px-3 py-2 rounded-lg hover:bg-yellow-600 transition-colors flex items-center gap-1 text-sm"
                >
                  <Edit size={14} />
                  <span className="hidden sm:inline">تعديل</span>
                </button>
              ) : (
                <div className="p-2 bg-slate-700/50 rounded-lg">
                  <Lock className="w-4 h-4 text-slate-500" />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  </div>
);

  const renderAccountStats = () => (
    <div className="space-y-6" dir="rtl">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 bg-yellow-500/10 rounded-lg">
          <Activity className="text-yellow-500 w-5 h-5" />
        </div>
        <h3 className="text-lg md:text-xl font-bold text-yellow-500">
          إحصائيات الحساب
        </h3>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-gray-900/50 backdrop-blur-sm p-4 md:p-6 rounded-xl border border-gray-800/50 hover:border-yellow-500/30 transition-all duration-300">
          <div className="flex items-center gap-2 text-yellow-500 mb-4">
            <ShoppingCart size={18} />
            <h4 className="font-semibold text-sm md:text-base">الإحصائيات الكلية</h4>
          </div>
          <div className="space-y-3">
            <div className="flex justify-between text-gray-300 text-sm">
              <span>إجمالي الطلبات:</span>
              <span className="font-medium text-white" dir="ltr">
                {account_stats?.total_orders || 0}
              </span>
            </div>
            <div className="flex justify-between text-gray-300 text-sm">
              <span>إجمالي الإيداعات:</span>
              <span className="font-medium text-white" dir="ltr">
                {parseFloat(
                  account_stats?.financial_summary?.total_deposits || 0
                ).toFixed(2)}
                $
              </span>
            </div>
            <div className="flex justify-between text-gray-300 text-sm">
              <span>إجمالي السحوبات:</span>
              <span className="font-medium text-white" dir="ltr">
                {parseFloat(
                  account_stats?.financial_summary?.total_withdrawals || 0
                ).toFixed(2)}
                $
              </span>
            </div>
            <div className="flex justify-between text-gray-300 text-sm">
              <span>عدد العمليات:</span>
              <span className="font-medium text-white" dir="ltr">
                {account_stats?.financial_summary?.transaction_count || 0}
              </span>
            </div>
          </div>
        </div>

        <div className="bg-gray-900/50 backdrop-blur-sm p-4 md:p-6 rounded-xl border border-gray-800/50 hover:border-yellow-500/30 transition-all duration-300">
          <div className="flex items-center gap-2 text-yellow-500 mb-4">
            <Calendar size={18} />
            <h4 className="font-semibold text-sm md:text-base">الأداء الشهري</h4>
          </div>
          <div className="space-y-3">
            <div className="flex justify-between text-gray-300 text-sm">
              <span>الطلبات المكتملة:</span>
              <span className="font-medium text-white" dir="ltr">
                {account_stats?.last_30_days?.completed_orders_count || 0}
              </span>
            </div>
            <div className="flex justify-between text-gray-300 text-sm">
              <span>إجمالي المصروفات:</span>
              <span className="font-medium text-white" dir="ltr">
                {parseFloat(
                  account_stats?.last_30_days?.total_spent || 0
                ).toFixed(2)}
                $
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderMembershipInfo = () => {
    return (
      <div className="space-y-6" dir="rtl">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-yellow-500/10 rounded-lg">
            <UserPlus className="text-yellow-500 w-5 h-5" />
          </div>
          <h3 className="text-lg md:text-xl font-bold text-yellow-500">
            تفاصيل العضوية
          </h3>
        </div>

        {!canUpdateUser && (
          <div className="flex items-center gap-2 p-3 bg-orange-900/20 border border-orange-500/30 rounded-lg">
            <AlertCircle className="w-5 h-5 text-orange-400" />
            <span className="text-orange-300 text-sm">
              ليس لديك صلاحية لتعديل مستوى العضوية
            </span>
          </div>
        )}

        <div className="bg-gray-900/50 backdrop-blur-sm p-4 md:p-6 rounded-xl border border-gray-800/50 hover:border-yellow-500/30 transition-all duration-300">
          <div className="flex items-center gap-2 text-yellow-500 mb-6">
            <Star size={18} />
            <h4 className="font-semibold text-sm md:text-base">مستوى المستخدم</h4>
          </div>

          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
              <span className="text-gray-300 text-sm md:text-base">اسم المستوى</span>
              <div className="relative w-full sm:w-auto">
                <button
                  onClick={() => canUpdateUser && setIsDropdownOpen(!isDropdownOpen)}
                  disabled={loading || !isManualLevel || !canUpdateUser}
                  className={`flex items-center justify-between gap-2 px-4 py-2 rounded-lg transition-colors w-full sm:w-auto min-w-[180px] text-sm ${
                    isManualLevel && canUpdateUser
                      ? "bg-gray-800 text-white hover:bg-gray-700 border border-gray-700"
                      : "bg-gray-700 text-gray-400 cursor-not-allowed border border-gray-600"
                  }`}
                >
                  {loading || isLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <span className="truncate">
                        {membership_info?.user_level?.name || "اختر المستوى"}
                      </span>
                      {!canUpdateUser && <Lock className="w-3 h-3" />}
                      <ChevronDown
                        className={`w-4 h-4 transition-transform flex-shrink-0 ${
                          isDropdownOpen ? "rotate-180" : ""
                        }`}
                      />
                    </>
                  )}
                </button>

                {isDropdownOpen && isManualLevel && canUpdateUser && (
                  <div className="absolute right-0 mt-2 w-full sm:w-48 bg-gray-800 rounded-lg shadow-xl border border-gray-700 z-50 max-h-60 overflow-y-auto">
                    {userLevels?.map((level) => (
                      <button
                        key={level.id}
                        onClick={() => handleLevelChange(level.id)}
                        className="flex items-center gap-2 w-full px-4 py-2 text-white hover:bg-gray-700 transition-colors text-sm"
                      >
                        {level.hex_color && (
                          <div
                            className="w-2 h-2 rounded-full flex-shrink-0"
                            style={{ backgroundColor: level.hex_color }}
                          />
                        )}
                        <span className="truncate">{level.name}</span>
                        {level.id === membership_info?.user_level?.id && (
                          <Check className="w-4 h-4 ml-auto text-green-500 flex-shrink-0" />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <SwitchItem
              label="تعيين يدوي"
              checked={isManualLevel}
              onChange={handleManualLevelToggle}
              icon={<User size={16} />}
              description={
                isManualLevel
                  ? "يمكنك تغيير المستوى يدويًا"
                  : "سيتم تحديد المستوى تلقائيًا"
              }
            />

            {membership_info?.user_level && (
              <div className="space-y-3 mt-4 p-4 bg-gray-800/50 rounded-lg border border-gray-700/50">
                <div className="flex justify-between text-gray-300 text-sm">
                  <span>نسبة الربح</span>
                  <span className="text-white font-medium">
                    {membership_info.user_level.profit_percentage}%
                  </span>
                </div>
                {membership_info.user_level.requirements && (
                  <div className="flex justify-between text-gray-300 text-sm">
                    <span>متطلبات المستوى</span>
                    <span className="text-white font-medium">
                      {membership_info.user_level.requirements}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderUserDeposits = () => {
    const depositTransactions = recent_transactions?.filter(
      (transaction) => transaction.type === "إيداع"
    ) || [];

    const formatDate = (dateString) => {
      return dayjs(dateString).format("DD/MM/YYYY HH:mm");
    };

    const totalDeposits = depositTransactions.reduce(
      (sum, transaction) => sum + parseFloat(transaction.amount || 0),
      0
    );

    return (
      <div className="space-y-4" dir="rtl">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-500/10 rounded-lg">
              <DollarSign className="text-yellow-500 w-5 h-5" />
            </div>
            <h3 className="text-lg md:text-xl font-bold text-yellow-500">
              إيداعات المستخدم
            </h3>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-xs md:text-sm text-yellow-500 font-bold">
              {depositTransactions.length} إيداع
            </div>
            <div className="text-xs md:text-sm text-green-500 font-bold">
              إجمالي: {totalDeposits.toFixed(2)}$
            </div>
          </div>
        </div>

        {depositTransactions.length === 0 ? (
          <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-8 md:p-12 text-center border border-gray-800/50">
            <DollarSign className="w-12 h-12 md:w-16 md:h-16 text-gray-500 mx-auto mb-4" />
            <h4 className="text-lg md:text-xl font-semibold text-gray-400 mb-2">
              لا توجد إيداعات
            </h4>
            <p className="text-sm text-gray-500">
              لم يقم هذا المستخدم بأي عمليات إيداع حتى الآن
            </p>
          </div>
        ) : (
          <div className="space-y-3 max-h-[600px] overflow-y-auto custom-scrollbar pr-1">
            {depositTransactions.map((transaction) => (
              <div
                key={transaction.id}
                className="bg-gray-900/50 backdrop-blur-sm p-4 md:p-6 rounded-xl border border-green-500/20 hover:border-green-500/40 transition-all duration-300"
              >
                <div className="flex flex-col sm:flex-row items-start justify-between gap-4">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className="bg-green-500/10 p-2 md:p-3 rounded-lg flex-shrink-0">
                      <TrendingUp className="w-4 h-4 md:w-5 md:h-5 text-green-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-white font-medium flex items-center gap-2 text-base md:text-lg flex-wrap">
                        <span>+{parseFloat(transaction.amount).toFixed(2)}$</span>
                        <span className="text-green-500 text-xs md:text-sm">
                          (
                          {(
                            (transaction.amount / (transaction.balance_before || 1)) *
                            100
                          ).toFixed(1)}
                          %)
                        </span>
                      </div>
                      <div className="text-xs md:text-sm text-gray-400 mt-1">
                        <span className="bg-green-500/20 text-green-400 px-2 py-1 rounded-full text-xs">
                          إيداع
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="text-right flex-shrink-0 w-full sm:w-auto">
                    <div className="text-xs md:text-sm text-gray-300 font-medium truncate">
                      {transaction.reference_id}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {formatDate(transaction.date)}
                    </div>
                  </div>
                </div>

                {transaction.description && (
                  <div className="mt-3 text-xs md:text-sm text-gray-400 border-t border-gray-700/50 pt-3">
                    {transaction.description}
                  </div>
                )}

                <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
                  <div className="text-gray-500">
                    الرصيد السابق:{" "}
                    {parseFloat(transaction.balance_before || 0).toFixed(2)}$
                  </div>
                  <ArrowLeft className="w-3 h-3 text-gray-600" />
                  <div className="text-green-400">
                    الرصيد الجديد:{" "}
                    {parseFloat(transaction.balance_after || 0).toFixed(2)}$
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderRecentTransactions = () => {
    const getTransactionColor = (type) => {
      switch (type) {
        case "إلغاء طلب":
          return "rose";
        case "اكتمال طلب":
          return "emerald";
        default:
          return "yellow";
      }
    };

    const getTransactionIcon = (type) => {
      switch (type) {
        case "إلغاء طلب":
          return <ArrowUpRight className={`w-4 h-4 text-rose-500`} />;
        case "اكتمال طلب":
          return <ArrowDownLeft className={`w-4 h-4 text-emerald-500`} />;
        default:
          return <ArrowRight className={`w-4 h-4 text-yellow-500`} />;
      }
    };

    const formatDate = (dateString) => {
      return dayjs(dateString).format("DD/MM/YYYY HH:mm");
    };

    return (
      <div className="space-y-4" dir="rtl">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-500/10 rounded-lg">
              <History className="text-yellow-500 w-5 h-5" />
            </div>
            <h3 className="text-lg md:text-xl font-bold text-yellow-500">
              المعاملات الأخيرة
            </h3>
          </div>
          <div className="text-xs md:text-sm text-yellow-500 font-bold">
            {recent_transactions?.length || 0} معاملة
          </div>
        </div>

        <div className="space-y-3 max-h-[600px] overflow-y-auto custom-scrollbar pr-1">
          {recent_transactions?.map((transaction) => {
            const color = getTransactionColor(transaction.type);
            const icon = getTransactionIcon(transaction.type);
            const isPositive = transaction.type === "إلغاء طلب";

            return (
              <div
                key={transaction.id}
                className="bg-gray-900/50 backdrop-blur-sm p-4 md:p-6 rounded-xl border border-gray-800/50 hover:border-gray-700 transition-all duration-300"
              >
                <div className="flex flex-col sm:flex-row items-start justify-between gap-4">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className={`bg-${color}-500/10 p-2 md:p-3 rounded-lg flex-shrink-0`}>
                      {icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-white font-medium flex items-center gap-2 text-base md:text-lg flex-wrap">
                        <span>
                          {isPositive ? "+" : "-"}
                          {parseFloat(transaction.amount).toFixed(2)}$
                        </span>
                        <span className={`text-${color}-500 text-xs md:text-sm`}>
                          (
                          {(
                            (transaction.amount / (transaction.balance_before || 1)) *
                            100
                          ).toFixed(1)}
                          %)
                        </span>
                      </div>
                      <div className="text-xs md:text-sm text-gray-400 mt-1">
                        {transaction.type}
                      </div>
                    </div>
                  </div>

                  <div className="text-right flex-shrink-0 w-full sm:w-auto">
                    <div className="text-xs md:text-sm text-gray-300 font-medium truncate">
                      {transaction.reference_id}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {formatDate(transaction.date)}
                    </div>
                  </div>
                </div>

                <div className="mt-3 text-xs md:text-sm text-gray-400 border-t border-gray-700/50 pt-3">
                  {transaction.description}
                </div>

                <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
                  <div className="text-gray-500">
                    الرصيد السابق:{" "}
                    {parseFloat(transaction.balance_before || 0).toFixed(2)}$
                  </div>
                  <ArrowLeft className="w-3 h-3 text-gray-600" />
                  <div className="text-gray-500">
                    الرصيد الجديد:{" "}
                    {parseFloat(transaction.balance_after || 0).toFixed(2)}$
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // الأقسام الجديدة من الـ API الجديد

  const renderFinancialSummary = () => {
    if (financialLoading.financial && !financialDetails) {
      return <LoadingSpinner message="جاري تحميل الملخص المالي..." />;
    }

    if (financialError.financial) {
      return (
        <div className="text-center py-12">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <p className="text-red-400">{financialError.financial}</p>
        </div>
      );
    }

    return (
      <div className="space-y-4" dir="rtl">
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-yellow-500/10 rounded-lg">
            <TrendingUp className="text-yellow-500 w-5 h-5" />
          </div>
          <h3 className="text-lg md:text-xl font-bold text-yellow-500">
            الملخص المالي الشامل
          </h3>
        </div>

        {/* ✅ الـ 3 عناصر في صف واحد - بالضبط زي الصورة */}
        <div className="grid grid-cols-3 gap-2">
          {/* إجمالي الإنفاق */}
          <div className="bg-[#1a1d29] rounded-lg p-2.5 sm:p-3 border border-gray-800/50">
            <p className="text-[9px] sm:text-xs text-gray-400 mb-1 text-center whitespace-nowrap">
              إجمالي الإنفاق
            </p>
            <p className="text-sm sm:text-lg font-bold text-white text-center">
              ${parseFloat(financialSummary?.total_spent || 0).toFixed(2)}
            </p>
          </div>

          {/* إجمالي الربح */}
          <div className="bg-[#1a1d29] rounded-lg p-2.5 sm:p-3 border border-gray-800/50">
            <p className="text-[9px] sm:text-xs text-green-400 mb-1 text-center whitespace-nowrap">
              إجمالي الربح
            </p>
            <p className="text-sm sm:text-lg font-bold text-green-400 text-center">
              ${parseFloat(financialSummary?.total_profit_generated || 0).toFixed(2)}
            </p>
          </div>

          {/* متوسط هامش الربح */}
          <div className="bg-[#3949ab] rounded-lg p-2.5 sm:p-3 border border-blue-600/50">
            <p className="text-[9px] sm:text-xs text-blue-100 mb-1 text-center whitespace-nowrap">
              متوسط هامش الربح
            </p>
            <p className="text-sm sm:text-lg font-bold text-white text-center">
              ${parseFloat(financialSummary?.average_profit_margin || 0).toFixed(2)}
            </p>
          </div>
        </div>

        {/* ✅ باقي الإحصائيات - 2 في صف على الموبايل */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {/* إجمالي الإيداعات */}
          <div className="bg-gradient-to-br from-green-500/10 to-green-600/5 rounded-xl p-4 border border-green-500/30">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-green-400">إجمالي الإيداعات</span>
              <TrendingUp className="w-4 h-4 text-green-400" />
            </div>
            <p className="text-lg font-bold text-white">
              ${parseFloat(financialSummary?.total_deposits || 0).toFixed(2)}
            </p>
          </div>

          {/* إجمالي السحوبات */}
          <div className="bg-gradient-to-br from-red-500/10 to-red-600/5 rounded-xl p-4 border border-red-500/30">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-red-400">إجمالي السحوبات</span>
              <TrendingDown className="w-4 h-4 text-red-400" />
            </div>
            <p className="text-lg font-bold text-white">
              ${parseFloat(financialSummary?.total_withdrawals || 0).toFixed(2)}
            </p>
          </div>

          {/* إجمالي الطلبات */}
          <div className="bg-gradient-to-br from-purple-500/10 to-purple-600/5 rounded-xl p-4 border border-purple-500/30">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-purple-400">إجمالي الطلبات</span>
              <ShoppingCart className="w-4 h-4 text-purple-400" />
            </div>
            <p className="text-lg font-bold text-white">
              {financialSummary?.total_orders || 0}
            </p>
            <p className="text-[10px] text-gray-400 mt-1">
              مكتمل: {financialSummary?.completed_orders || 0}
            </p>
          </div>

          {/* رصيد المحفظة */}
          <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700/50">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-blue-400">رصيد المحفظة</span>
              <Wallet className="w-4 h-4 text-blue-400" />
            </div>
            <p className="text-lg font-bold text-white">
              ${parseFloat(financialSummary?.wallet_balance || 0).toFixed(2)}
            </p>
          </div>

          {/* إجمالي الديون */}
          <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700/50">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-orange-400">إجمالي الديون</span>
              <AlertCircle className="w-4 h-4 text-orange-400" />
            </div>
            <p className="text-lg font-bold text-white">
              ${parseFloat(financialSummary?.total_debt || 0).toFixed(2)}
            </p>
            <p className="text-[10px] text-gray-400 mt-1">
              الحد: ${parseFloat(financialSummary?.max_debt_limit || 0).toFixed(2)}
            </p>
          </div>

          {/* طلبات السحب */}
          <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700/50">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-pink-400">طلبات السحب</span>
              <TrendingDown className="w-4 h-4 text-pink-400" />
            </div>
            <p className="text-lg font-bold text-white">
              {financialSummary?.withdrawal_requests_count || 0}
            </p>
          </div>
        </div>
      </div>
    );
  };

    const renderProductPerformance = () => {
    if (financialLoading.financial && !financialDetails) {
      return <LoadingSpinner message="جاري تحميل أداء المنتجات..." />;
    }

    if (financialError.financial) {
      return (
        <div className="text-center py-12">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <p className="text-red-400">{financialError.financial}</p>
        </div>
      );
    }

    return (
      <div className="space-y-4" dir="rtl">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-yellow-500/10 rounded-lg">
            <Package className="text-yellow-500 w-5 h-5" />
          </div>
          <h3 className="text-lg md:text-xl font-bold text-yellow-500">
            أداء المنتجات
          </h3>
        </div>

        {productPerformance.length > 0 ? (
          productPerformance.map((product) => (
            <div
              key={product.product_id}
              className="bg-gray-800/50 rounded-xl p-6 border border-gray-700/50"
            >
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
                <div>
                  <h3 className="text-lg font-bold text-white mb-1">
                    {product.product_name}
                  </h3>
                  <p className="text-sm text-gray-400">
                    آخر طلب: {new Date(product.last_order_date).toLocaleDateString("ar-EG")}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className={`px-3 py-1 rounded-full text-sm font-medium ${
                      parseFloat(product.success_rate) >= 70
                        ? "bg-green-500/10 text-green-400 border border-green-500/30"
                        : parseFloat(product.success_rate) >= 40
                        ? "bg-yellow-500/10 text-yellow-400 border border-yellow-500/30"
                        : "bg-red-500/10 text-red-400 border border-red-500/30"
                    }`}
                  >
                    نسبة النجاح: {product.success_rate}%
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="bg-blue-900/50 rounded-lg p-3 border border-blue-500/30">
                  <p className="text-xs text-gray-400 mb-1">إجمالي الطلبات</p>
                  <p className="text-xl font-bold text-white">{product.total_orders}</p>
                </div>
                <div className="bg-green-500/10 rounded-lg p-3 border border-green-500/30">
                  <p className="text-xs text-green-400 mb-1">مكتمل</p>
                  <p className="text-xl font-bold text-white">{product.completed_orders}</p>
                </div>
                <div className="bg-red-500/10 rounded-lg p-3 border border-red-500/30">
                  <p className="text-xs text-red-400 mb-1">ملغي</p>
                  <p className="text-xl font-bold text-white">{product.cancelled_orders}</p>
                </div>
                <div className="bg-yellow-500/10 rounded-lg p-3 border border-yellow-500/30">
                  <p className="text-xs text-yellow-400 mb-1">معلق</p>
                  <p className="text-xl font-bold text-white">{product.pending_orders}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
                <div className="bg-gray-900/50 rounded-lg p-3">
                  <p className="text-xs text-gray-400 mb-1">إجمالي الإنفاق</p>
                  <p className="text-lg font-bold text-white">
                    ${parseFloat(product.total_spent).toFixed(2)}
                  </p>
                </div>
                <div className="bg-gray-900/50 rounded-lg p-3">
                  <p className="text-xs text-gray-400 mb-1">إجمالي الربح</p>
                  <p className="text-lg font-bold text-green-400">
                    ${parseFloat(product.total_profit).toFixed(2)}
                  </p>
                </div>
                <div className="bg-gray-900/50 rounded-lg p-3">
                  <p className="text-xs text-gray-400 mb-1">متوسط هامش الربح</p>
                  <p className="text-lg font-bold text-purple-400">
                    ${parseFloat(product.average_profit_margin).toFixed(2)}
                  </p>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-12">
            <Package className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400">لا توجد منتجات بعد</p>
          </div>
        )}
      </div>
    );
  };


  const renderTransactionHistory = () => {
    // Loading State - عند التحميل الأولي
    if (financialLoading.transactions && transactionHistory.results.length === 0) {
      return (
        <div className="space-y-4" dir="rtl">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-yellow-500/10 rounded-lg">
              <History className="text-yellow-500 w-5 h-5" />
            </div>
            <h3 className="text-lg md:text-xl font-bold text-yellow-500">
              سجل المعاملات المالية
            </h3>
          </div>
          <LoadingSpinner message="جاري تحميل المعاملات المالية..." />
        </div>
      );
    }

    return (
      <div className="space-y-4" dir="rtl">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-yellow-500/10 rounded-lg">
            <History className="text-yellow-500 w-5 h-5" />
          </div>
          <h3 className="text-lg md:text-xl font-bold text-yellow-500">
            سجل المعاملات المالية
          </h3>
        </div>

        {/* Filters */}
        <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700/50">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="w-5 h-5 text-yellow-400" />
            <h3 className="text-lg font-bold text-white">تصفية النتائج</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <select
              value={transactionFilters.type}
              onChange={(e) => handleFilterChange("type", e.target.value)}
              disabled={financialLoading.transactions}
              className="bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600 focus:border-yellow-500 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <option value="">كل الأنواع</option>
              <option value="deposit">إيداع</option>
              <option value="withdrawal">سحب</option>
            </select>

            <select
              value={transactionFilters.status}
              onChange={(e) => handleFilterChange("status", e.target.value)}
              disabled={financialLoading.transactions}
              className="bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600 focus:border-yellow-500 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <option value="">كل الحالات</option>
              <option value="paid">مكتمل</option>
              <option value="failed">فشل</option>
              <option value="pending">معلق</option>
            </select>

            <input
              type="date"
              value={transactionFilters.dateFrom}
              onChange={(e) => handleFilterChange("dateFrom", e.target.value)}
              disabled={financialLoading.transactions}
              className="bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600 focus:border-yellow-500 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
              placeholder="من تاريخ"
            />

            <input
              type="date"
              value={transactionFilters.dateTo}
              onChange={(e) => handleFilterChange("dateTo", e.target.value)}
              disabled={financialLoading.transactions}
              className="bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600 focus:border-yellow-500 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
              placeholder="إلى تاريخ"
            />
          </div>
          
          {/* زر مسح الفلاتر */}
          {(transactionFilters.type ||
            transactionFilters.status ||
            transactionFilters.dateFrom ||
            transactionFilters.dateTo) && (
            <div className="mt-4">
              <button
                onClick={clearFilters}
                disabled={financialLoading.transactions}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <XCircle className="w-4 h-4" />
                مسح الفلاتر
              </button>
            </div>
          )}
        </div>

        {/* Summary */}
        {transactionHistory.summary && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700/50">
              <p className="text-xs text-gray-400 mb-1">إجمالي المبلغ</p>
              <p className="text-xl font-bold text-white">
                ${parseFloat(transactionHistory.summary.total_amount).toFixed(2)}
              </p>
            </div>
            <div className="bg-green-500/10 rounded-xl p-4 border border-green-500/30">
              <p className="text-xs text-green-400 mb-1">ناجح</p>
              <p className="text-xl font-bold text-white">
                {transactionHistory.summary.successful_count}
              </p>
            </div>
            <div className="bg-red-500/10 rounded-xl p-4 border border-red-500/30">
              <p className="text-xs text-red-400 mb-1">فشل</p>
              <p className="text-xl font-bold text-white">
                {transactionHistory.summary.failed_count}
              </p>
            </div>
            <div className="bg-yellow-500/10 rounded-xl p-4 border border-yellow-500/30">
              <p className="text-xs text-yellow-400 mb-1">معلق</p>
              <p className="text-xl font-bold text-white">
                {transactionHistory.summary.pending_count}
              </p>
            </div>
          </div>
        )}

        {/* Transactions List */}
        <div className="space-y-4">
          {transactionHistory.results.map((transaction) => (
            <TransactionCard key={transaction.transaction_id} transaction={transaction} />
          ))}

          {/* Load More Trigger - Infinite Scroll */}
          {transactionHistory.hasMore && (
            <div 
              ref={transactionsLoadMoreRef} 
              className="py-8 flex flex-col items-center justify-center gap-2"
            >
              {financialLoading.transactions ? (
                <LoadingSpinner message="جاري تحميل المزيد من المعاملات..." />
              ) : (
                <div className="text-center">
                  <div className="flex items-center justify-center gap-2 text-gray-400 text-sm mb-2 animate-pulse">
                    <ArrowDownLeft className="w-4 h-4" />
                    <span>قم بالتمرير لتحميل المزيد</span>
                  </div>
                  <div className="text-xs text-gray-500">
                    صفحة {transactionHistory.currentPage} من {transactionHistory.totalPages}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Status Messages */}
          {!financialLoading.transactions &&
            !transactionHistory.hasMore &&
            transactionHistory.results.length > 0 && (
              <p className="text-center text-gray-400 py-4 border-t border-gray-700 flex items-center justify-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-400" />
                تم تحميل جميع المعاملات ({transactionHistory.count} معاملة)
              </p>
            )}

          {!financialLoading.transactions && transactionHistory.results.length === 0 && (
            <div className="text-center py-12">
              <DollarSign className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">لا توجد معاملات</p>
            </div>
          )}

          {/* Page Info */}
          {transactionHistory.results.length > 0 && (
            <div className="text-center text-sm text-gray-500 py-2 flex items-center justify-center gap-2">
              <Activity className="w-4 h-4" />
              <span>
                عرض {transactionHistory.results.length} من {transactionHistory.count} معاملة
                (صفحة {transactionHistory.currentPage} من {transactionHistory.totalPages})
              </span>
            </div>
          )}
        </div>
      </div>
    );
  };



  const renderWalletLogs = () => {
    // Loading State - عند التحميل الأولي
    if (financialLoading.walletLogs && walletLogs.results.length === 0) {
      return (
        <div className="space-y-4" dir="rtl">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-yellow-500/10 rounded-lg">
              <Wallet className="text-yellow-500 w-5 h-5" />
            </div>
            <h3 className="text-lg md:text-xl font-bold text-yellow-500">
              سجل المحفظة التفصيلي
            </h3>
          </div>
          <LoadingSpinner message="جاري تحميل سجل المحفظة..." />
        </div>
      );
    }

    return (
      <div className="space-y-4" dir="rtl">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-yellow-500/10 rounded-lg">
            <Wallet className="text-yellow-500 w-5 h-5" />
          </div>
          <h3 className="text-lg md:text-xl font-bold text-yellow-500">
            سجل المحفظة التفصيلي
          </h3>
        </div>

        {/* Summary */}
        {walletLogs.summary && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700/50">
              <p className="text-xs text-gray-400 mb-1">الرصيد الحالي</p>
              <p className="text-2xl font-bold text-white">
                ${parseFloat(walletLogs.summary.current_balance).toFixed(2)}
              </p>
            </div>
            <div className="bg-green-500/10 rounded-xl p-4 border border-green-500/30">
              <p className="text-xs text-green-400 mb-1">إجمالي الإضافات</p>
              <p className="text-2xl font-bold text-white">
                ${parseFloat(walletLogs.summary.total_credits).toFixed(2)}
              </p>
            </div>
            <div className="bg-red-500/10 rounded-xl p-4 border border-red-500/30">
              <p className="text-xs text-red-400 mb-1">إجمالي الخصومات</p>
              <p className="text-2xl font-bold text-white">
                ${parseFloat(walletLogs.summary.total_debits).toFixed(2)}
              </p>
            </div>
          </div>
        )}

        {/* Wallet Logs List */}
        <div className="space-y-4">
          {walletLogs.results.map((log) => (
            <WalletLogCard key={log.transaction_id} log={log} />
          ))}

          {/* Load More Trigger */}
          {walletLogs.hasMore && (
            <div 
              ref={walletLogsLoadMoreRef} 
              className="py-8 flex flex-col items-center justify-center gap-2"
            >
              {financialLoading.walletLogs ? (
                <LoadingSpinner message="جاري تحميل المزيد من السجلات..." />
              ) : (
                <div className="text-center">
                  <div className="flex items-center justify-center gap-2 text-gray-400 text-sm mb-2 animate-pulse">
                    <ArrowDownLeft className="w-4 h-4" />
                    <span>قم بالتمرير لتحميل المزيد</span>
                  </div>
                  <div className="text-xs text-gray-500">
                    صفحة {walletLogs.currentPage} من {walletLogs.totalPages}
                  </div>
                </div>
              )}
            </div>
          )}

          {!financialLoading.walletLogs && !walletLogs.hasMore && walletLogs.results.length > 0 && (
            <p className="text-center text-gray-400 py-4 border-t border-gray-700 flex items-center justify-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-400" />
              تم تحميل جميع السجلات ({walletLogs.count} سجل)
            </p>
          )}

          {!financialLoading.walletLogs && walletLogs.results.length === 0 && (
            <div className="text-center py-12">
              <Wallet className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">لا توجد سجلات للمحفظة</p>
            </div>
          )}

          {/* Page Info */}
          {walletLogs.results.length > 0 && (
            <div className="text-center text-sm text-gray-500 py-2 flex items-center justify-center gap-2">
              <Activity className="w-4 h-4" />
              <span>
                عرض {walletLogs.results.length} من {walletLogs.count} سجل
                (صفحة {walletLogs.currentPage} من {walletLogs.totalPages})
              </span>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderAccountStatus = () => {
    if (financialLoading.financial && !financialDetails) {
      return <LoadingSpinner message="جاري تحميل حالة الحساب..." />;
    }

    return (
      <div className="space-y-4" dir="rtl">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-yellow-500/10 rounded-lg">
            <CheckCircle2 className="text-yellow-500 w-5 h-5" />
          </div>
          <h3 className="text-lg md:text-xl font-bold text-yellow-500">
            حالة الحساب المتقدمة
          </h3>
        </div>

        <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700/50">
          <h3 className="text-lg font-bold text-white mb-4">حالة الحساب</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex items-center justify-between p-4 bg-gray-900/50 rounded-lg">
              <span className="text-gray-400">حالة المحفظة</span>
              {accountStatus?.is_wallet_frozen ? (
                <span className="flex items-center gap-2 text-red-400">
                  <XCircle className="w-5 h-5" />
                  مجمدة
                </span>
              ) : (
                <span className="flex items-center gap-2 text-green-400">
                  <CheckCircle2 className="w-5 h-5" />
                  نشطة
                </span>
              )}
            </div>

            <div className="flex items-center justify-between p-4 bg-gray-900/50 rounded-lg">
              <span className="text-gray-400">إمكانية الشراء</span>
              {accountStatus?.can_purchase ? (
                <span className="flex items-center gap-2 text-green-400">
                  <CheckCircle2 className="w-5 h-5" />
                  متاح
                </span>
              ) : (
                <span className="flex items-center gap-2 text-red-400">
                  <XCircle className="w-5 h-5" />
                  غير متاح
                </span>
              )}
            </div>

            <div className="flex items-center justify-between p-4 bg-gray-900/50 rounded-lg">
              <span className="text-gray-400">مستوى المخاطرة</span>
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  accountStatus?.risk_level === "LOW"
                    ? "bg-green-500/10 text-green-400 border border-green-500/30"
                    : accountStatus?.risk_level === "MEDIUM"
                    ? "bg-yellow-500/10 text-yellow-400 border border-yellow-500/30"
                    : "bg-red-500/10 text-red-400 border border-red-500/30"
                }`}
              >
                {accountStatus?.risk_level}
              </span>
            </div>

            <div className="flex items-center justify-between p-4 bg-gray-900/50 rounded-lg">
              <span className="text-gray-400">استخدام API</span>
              {accountStatus?.can_use_api_key ? (
                <span className="flex items-center gap-2 text-green-400">
                  <CheckCircle2 className="w-5 h-5" />
                  مفعّل
                </span>
              ) : (
                <span className="flex items-center gap-2 text-red-400">
                  <XCircle className="w-5 h-5" />
                  غير مفعّل
                </span>
              )}
            </div>
          </div>

          {accountStatus?.risk_factors && accountStatus.risk_factors.length > 0 && (
            <div className="mt-4 p-4 bg-red-500/10 rounded-lg border border-red-500/30">
              <h4 className="text-red-400 font-medium mb-2">عوامل المخاطرة:</h4>
              <ul className="list-disc list-inside text-sm text-gray-400 space-y-1">
                {accountStatus.risk_factors.map((factor, index) => (
                  <li key={index}>{factor}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderRecentActivity = () => {
    if (financialLoading.financial && !financialDetails) {
      return <LoadingSpinner message="جاري تحميل النشاط الأخير..." />;
    }

    return (
      <div className="space-y-4" dir="rtl">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-yellow-500/10 rounded-lg">
            <Calendar className="text-yellow-500 w-5 h-5" />
          </div>
          <h3 className="text-lg md:text-xl font-bold text-yellow-500">
            النشاط الأخير
          </h3>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700/50">
            <div className="flex items-center justify-between mb-4">
              <Calendar className="w-8 h-8 text-purple-400" />
              <span className="text-xs text-purple-400">آخر 30 يوم</span>
            </div>
            <p className="text-xs text-gray-400 mb-1">الطلبات</p>
            <p className="text-2xl font-bold text-white">
              {recentActivity?.orders_last_30_days || 0}
            </p>
          </div>

          <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700/50">
            <div className="flex items-center justify-between mb-4">
              <Calendar className="w-8 h-8 text-blue-400" />
              <span className="text-xs text-blue-400">آخر 7 أيام</span>
            </div>
            <p className="text-xs text-gray-400 mb-1">الطلبات</p>
            <p className="text-2xl font-bold text-white">
              {recentActivity?.orders_last_7_days || 0}
            </p>
          </div>

          <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700/50">
            <div className="flex items-center justify-between mb-4">
              <CheckCircle2 className="w-8 h-8 text-green-400" />
              <span className="text-xs text-green-400">مكتمل (30 يوم)</span>
            </div>
            <p className="text-xs text-gray-400 mb-1">الطلبات</p>
            <p className="text-2xl font-bold text-white">
              {recentActivity?.completed_orders_30_days || 0}
            </p>
          </div>

          <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700/50">
            <div className="flex items-center justify-between mb-4">
              <DollarSign className="w-8 h-8 text-yellow-400" />
              <span className="text-xs text-yellow-400">القيمة الإجمالية</span>
            </div>
            <p className="text-xs text-gray-400 mb-1">آخر 30 يوم</p>
            <p className="text-2xl font-bold text-white">
              ${parseFloat(recentActivity?.total_value_30_days || 0).toFixed(2)}
            </p>
          </div>

          <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700/50">
            <div className="flex items-center justify-between mb-4">
              <TrendingUp className="w-8 h-8 text-green-400" />
              <span className="text-xs text-green-400">الإيداعات</span>
            </div>
            <p className="text-xs text-gray-400 mb-1">آخر 30 يوم</p>
            <p className="text-2xl font-bold text-white">
              {recentActivity?.deposits_last_30_days || 0}
            </p>
          </div>

          <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700/50">
            <div className="flex items-center justify-between mb-4">
              <TrendingDown className="w-8 h-8 text-red-400" />
              <span className="text-xs text-red-400">السحوبات</span>
            </div>
            <p className="text-xs text-gray-400 mb-1">آخر 30 يوم</p>
            <p className="text-2xl font-bold text-white">
              {recentActivity?.withdrawals_last_30_days || 0}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700/50">
            <h3 className="text-lg font-bold text-white mb-3">آخر طلب</h3>
            <p className="text-sm text-gray-400">
              {recentActivity?.last_order_date
                ? new Date(recentActivity.last_order_date).toLocaleString("ar-EG")
                : "لا يوجد"}
            </p>
          </div>

          <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700/50">
            <h3 className="text-lg font-bold text-white mb-3">آخر معاملة</h3>
            <p className="text-sm text-gray-400">
              {recentActivity?.last_transaction_date
                ? new Date(recentActivity.last_transaction_date).toLocaleString("ar-EG")
                : "لا يوجد"}
            </p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen w-full bg-gray-950">
      {/* Fixed Header */}
      <div className="w-full bg-gray-900/95 backdrop-blur-md border-b border-gray-800/50 sticky top-16 z-40 shadow-xl">
        <div className="px-3 md:px-4 py-3 md:py-4">
          <div className="flex items-center justify-between gap-3">
            {/* Back button and title */}
            <div className="flex items-center gap-2 md:gap-4 flex-1 min-w-0">
              <button
                onClick={() => navigate("/user")}
                className="p-1.5 md:p-2 hover:bg-gray-800 rounded-full transition-colors flex-shrink-0"
              >
                <ChevronLeft className="text-gray-400 hover:text-white w-4 h-4 md:w-5 md:h-5" />
              </button>
              <div className="flex items-center gap-2 md:gap-3 flex-1 min-w-0">
                <div className="p-1.5 md:p-2 bg-yellow-500/10 rounded-lg flex-shrink-0">
                  <User className="text-yellow-500 w-4 h-4 md:w-5 md:h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <h1 className="text-sm md:text-xl font-bold text-white truncate">
                    الملف الشخصي
                  </h1>
                  <p className="text-xs text-gray-400 truncate">
                    {basic_info?.username}@
                  </p>
                </div>
              </div>
            </div>

            {/* Action button */}
              {canSendNotifications ? (
              <button
                onClick={() => setIsNotificationPopupVisible(true)}
                className="flex items-center gap-1.5 md:gap-2 px-2.5 md:px-4 py-1.5 md:py-2 bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-white rounded-lg transition-colors flex-shrink-0"
              >
                <Bell className="text-yellow-500 w-4 h-4" />
                <span className="hidden sm:inline text-xs md:text-sm">إشعار</span>
              </button>
            ) : (
              <div className="flex items-center gap-1.5 px-2.5 md:px-4 py-1.5 md:py-2 bg-slate-800/50 rounded-lg border border-slate-700">
                <Lock className="w-4 h-4 text-slate-500" />
                <span className="hidden sm:inline text-xs text-slate-400">إشعار</span>
              </div>
            )}
          </div>

          {/* Fixed Navigation tabs - Centered with flex-wrap */}
          <nav className="flex flex-wrap flex-row-reverse justify-center gap-2 mt-3 md:mt-4 pb-2">
            {navigationButtons.map(({ id, icon }) => (
              <button
                key={id}
                onClick={() => setActiveSection(id)}
                className={`flex items-center gap-1.5 md:gap-2 px-3 md:px-4 py-1.5 md:py-2 rounded-lg transition-all duration-300 whitespace-nowrap flex-shrink-0 flex-grow text-xs md:text-sm ${
                  activeSection === id
                    ? "bg-yellow-500 text-black shadow-lg shadow-yellow-500/20 scale-105"
                    : "bg-gray-800/50 text-gray-300 hover:bg-gray-700/50"
                }`}
              >
                {icon}
                <span className="hidden sm:inline">{id}</span>
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Scrollable Content with proper spacing */}
      <div className="px-2 md:px-4 py-4 md:py-6 mt-[10px] md:mt-[10px]">
        <div className="max-w-7xl mx-auto">
          {activeSection === "المعلومات الأساسية" && renderBasicInfo()}
          {activeSection === "الملخص المالي الشامل" && renderFinancialSummary()}
          {activeSection === "أداء المنتجات" && renderProductPerformance()}
          {activeSection === "إحصائيات الحساب" && renderAccountStats()}
          {activeSection === "إعدادات الأمان" && renderSecurityInfo()}
          {activeSection === "المعلومات المالية" && renderFinancialStats()}
          {activeSection === "تفاصيل العضوية" && renderMembershipInfo()}
          {activeSection === "إيداعات المستخدم" && renderUserDeposits()}
          {activeSection === "سجل المعاملات المالية" && renderTransactionHistory()}
          {activeSection === "سجل المحفظة التفصيلي" && renderWalletLogs()}
          {activeSection === "حالة الحساب المتقدمة" && renderAccountStatus()}
          {activeSection === "النشاط الأخير" && renderRecentActivity()}
        </div>
      </div>

      {/* Modals */}
      {isNotificationPopupVisible && canSendNotifications && (
        <NotificationPopup
          onClose={() => setIsNotificationPopupVisible(false)}
          onSubmit={handleNotificationSubmit}
          isLoading={isNotificationLoading}
        />
      )}
    </div>
  );
};

export default UserDetailsPage;